// #tag: OPPORTUNITY DETAIL SCREEN
// #purpose: Comprehensive job overview showing application countdown, stage timelines, required document checklists, salary bands, and vacancy analytics.
import React from 'react';
import { ArrowLeft, ExternalLink, Heart } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaqSection } from '../components/cms/PageSections';
import { EmptyState, ErrorState, LoadingState } from '../components/states/StateViews';
import { OpportunityCard } from '../components/opportunities/OpportunityCards';
import { DeadlineProgressBar } from '../components/opportunities/DeadlineProgressBar';
import { OpportunityTimeline } from '../components/opportunities/OpportunityTimeline';
import { RequiredDocumentsChecklist } from '../components/opportunities/RequiredDocumentsChecklist';
import { CompetitionBadge } from '../components/opportunities/CompetitionBadge';
import { SalaryCard } from '../components/opportunities/SalaryCard';
import { VacancyAnalytics } from '../components/opportunities/VacancyAnalytics';
import { PrepareSection } from '../components/opportunities/PrepareSection';
import { TrackOpportunityButton } from '../components/opportunities/TrackOpportunityButton';
import { useOpportunity, useOpportunities } from '../hooks/useExamData';
import { useSavedOpportunities } from '../hooks/useSavedOpportunities';
import { safeExternalUrl } from '../lib/safeExternalUrl';
import { hasSalaryCard, hasVacancyAnalytics } from '../lib/opportunityUi';

function formatDate(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function OpportunityDetailScreen() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: opportunity, isLoading, isError, refetch } = useOpportunity(slug);
  const { isSaved, toggleSave } = useSavedOpportunities();

  const { data: relatedData } = useOpportunities({
    opportunityType: opportunity?.opportunityType,
    limit: 3,
  });

  if (isLoading) {
    return (
      <section className="adaptive-page">
        <LoadingState label="Loading opportunity…" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="adaptive-page">
        <ErrorState message="Could not load this opportunity." onRetry={() => refetch()} />
      </section>
    );
  }

  if (!opportunity) {
    return (
      <section className="adaptive-page">
        <EmptyState
          title="Opportunity not found"
          message="This listing may have been removed or the link is incorrect."
          illustrationContext="empty.opportunities"
          action={
            <button type="button" className="primary-action" onClick={() => navigate('/opportunities')}>
              Back to opportunities
            </button>
          }
        />
      </section>
    );
  }

  const applyLink = safeExternalUrl(opportunity.officialApplyUrl || opportunity.officialWebsite);
  const notificationLink = safeExternalUrl(opportunity.officialNotification);
  const saved = isSaved(opportunity.id);
  const related = (relatedData?.data ?? []).filter((item) => item.id !== opportunity.id).slice(0, 3);
  const documents = opportunity.requiredDocuments ?? [];
  const timeline = opportunity.lifecycleTimeline ?? [];

  return (
    <section className="adaptive-page wfx-opportunity-detail">
      <button type="button" className="text-action wfx-back-link" onClick={() => navigate('/opportunities')}>
        <ArrowLeft size={16} /> Back to opportunities
      </button>

      <header className="wfx-opportunity-detail-head glass-surface">
        <div>
          <div className="wfx-opportunity-badges">
            {opportunity.isNew && <span className="chip-pill is-new">New</span>}
            {opportunity.isClosingSoon && <span className="chip-pill wfx-opp-status is-closing">Closing soon</span>}
            <span className="chip-pill">{opportunity.qualification}</span>
            <span className="chip-pill">{opportunity.state}</span>
          </div>
          <h1>{opportunity.title}</h1>
          <p className="wfx-muted">{opportunity.organization}</p>
          <DeadlineProgressBar opportunity={opportunity} />
        </div>
        <div className="wfx-opportunity-detail-actions">
          <TrackOpportunityButton opportunityId={opportunity.id} />
          <button
            type="button"
            className={`secondary-action compact${saved ? ' is-saved' : ''}`}
            onClick={() =>
              toggleSave({
                id: opportunity.id,
                slug: opportunity.slug,
                title: opportunity.title,
                organization: opportunity.organization,
                savedAt: new Date().toISOString(),
              })
            }
          >
            <Heart size={16} fill={saved ? 'currentColor' : 'none'} /> {saved ? 'Saved' : 'Save'}
          </button>
          {applyLink && opportunity.displayStatus !== 'closed' && (
            <a href={applyLink} className="primary-action compact" target="_blank" rel="noopener noreferrer">
              Apply now <ExternalLink size={14} />
            </a>
          )}
        </div>
      </header>

      {timeline.length > 0 && (
        <section className="glass-surface wfx-opportunity-panel wfx-opportunity-panel--wide">
          <h2>Application timeline</h2>
          <OpportunityTimeline stages={timeline} />
        </section>
      )}

      <div className="wfx-opportunity-detail-grid">
        <article className="glass-surface wfx-opportunity-panel">
          <h2>Overview</h2>
          <p>{opportunity.description}</p>
          {opportunity.eligibility && (
            <>
              <h3>Eligibility</h3>
              <p>{opportunity.eligibility}</p>
            </>
          )}
        </article>

        <aside className="glass-surface wfx-opportunity-panel">
          <h2>Important dates</h2>
          <ul className="wfx-opportunity-dates">
            <li><span>Application start</span><strong>{formatDate(opportunity.applicationStart)}</strong></li>
            <li><span>Last date</span><strong>{formatDate(opportunity.applicationEnd)}</strong></li>
            {opportunity.examDate && <li><span>Exam date</span><strong>{formatDate(opportunity.examDate)}</strong></li>}
            {(opportunity.importantDates ?? []).map((item) => (
              <li key={item.label}><span>{item.label}</span><strong>{formatDate(item.date)}</strong></li>
            ))}
          </ul>
        </aside>

        {(hasSalaryCard(opportunity) || hasVacancyAnalytics(opportunity) || opportunity.competitionLevel) && (
          <aside className="wfx-opportunity-panel wfx-opportunity-panel--wide wfx-opp-insights">
            {hasSalaryCard(opportunity) && <SalaryCard opportunity={opportunity} />}
            {hasVacancyAnalytics(opportunity) && <VacancyAnalytics opportunity={opportunity} />}
            {opportunity.competitionLevel != null && <CompetitionBadge opportunity={opportunity} />}
          </aside>
        )}

        <article className="glass-surface wfx-opportunity-panel">
          <h2>Details</h2>
          <dl className="wfx-opportunity-facts">
            {opportunity.vacancies != null && <div><dt>Vacancies</dt><dd>{opportunity.vacancies.toLocaleString('en-IN')}</dd></div>}
            {opportunity.salary && <div><dt>Salary / stipend</dt><dd>{opportunity.salary}</dd></div>}
            {opportunity.applicationFee && <div><dt>Application fee</dt><dd>{opportunity.applicationFee}</dd></div>}
            {opportunity.ageLimit && <div><dt>Age limit</dt><dd>{opportunity.ageLimit}</dd></div>}
            {opportunity.location && <div><dt>Location</dt><dd>{opportunity.location}</dd></div>}
          </dl>
        </article>

        {opportunity.selectionProcess && (
          <article className="glass-surface wfx-opportunity-panel">
            <h2>Selection process</h2>
            <p>{opportunity.selectionProcess}</p>
          </article>
        )}

        {opportunity.howToApply && (
          <article className="glass-surface wfx-opportunity-panel">
            <h2>How to apply</h2>
            <p>{opportunity.howToApply}</p>
          </article>
        )}

        {documents.length > 0 && (
          <article className="glass-surface wfx-opportunity-panel">
            <h2>Required documents</h2>
            <RequiredDocumentsChecklist opportunityId={opportunity.id} documents={documents} />
          </article>
        )}

        <article className="glass-surface wfx-opportunity-panel">
          <h2>Important links</h2>
          <div className="wfx-opportunity-links">
            {notificationLink && (
              <a href={notificationLink} target="_blank" rel="noopener noreferrer" className="secondary-action compact">
                Official notification <ExternalLink size={14} />
              </a>
            )}
            {applyLink && (
              <a href={applyLink} target="_blank" rel="noopener noreferrer" className="secondary-action compact">
                Official website <ExternalLink size={14} />
              </a>
            )}
          </div>
        </article>
      </div>

      <PrepareSection links={opportunity.preparationLinks ?? []} />

      {(opportunity.faqs?.length ?? 0) > 0 && (
        <FaqSection
          title="FAQs"
          items={opportunity.faqs!}
          kicker="About this opportunity"
          embedded
        />
      )}

      {related.length > 0 && (
        <section className="wfx-opp-section">
          <h2>Related opportunities</h2>
          <div className="wfx-result-grid">
            {related.map((item) => (
              <OpportunityCard key={item.id} opportunity={item} />
            ))}
          </div>
        </section>
      )}
    </section>
  );
}
